import React, { useRef, useState } from 'react';
import { Video, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const uploadChunkWithRetry = async (uploadId, chunkIndex, chunk, token, maxRetries = 3) => {
  let lastError = null;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const formData = new FormData();
      formData.append('chunk', chunk, `chunk_${chunkIndex}`);
      await axios.post(`${API}/upload/chunk/${uploadId}/${chunkIndex}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      return true;
    } catch (error) {
      lastError = error;
      if (attempt < maxRetries) {
        const delay = Math.min(2000 * Math.pow(2, attempt - 1), 10000);
        await new Promise((r) => setTimeout(r, delay));
      }
    }
  }
  throw lastError;
};

const TrailerUploadModal = ({ open, onClose, bookId, token, onUploaded }) => {
  const [trailerFile, setTrailerFile] = useState(null);
  const [trailerDuration, setTrailerDuration] = useState(0);
  const [trailerError, setTrailerError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  if (!open) return null;

  const handleSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setTrailerError('');
    if (!['video/mp4', 'video/webm'].includes(file.type)) {
      setTrailerError('Invalid file format. Only MP4 and WebM are accepted.');
      return;
    }
    if (file.size > 250 * 1024 * 1024) {
      setTrailerError('File size exceeds 250MB limit.');
      return;
    }
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      const duration = video.duration;
      if (duration > 150) {
        setTrailerError('Trailer must not exceed 2 minutes and 30 seconds (150 seconds).');
        setTrailerFile(null);
        return;
      }
      setTrailerDuration(duration);
      setTrailerFile(file);
    };
    video.src = URL.createObjectURL(file);
  };

  const reset = () => {
    setTrailerFile(null);
    setTrailerDuration(0);
    setTrailerError('');
    setProgress(0);
    setStatus('');
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  const handleUpload = async () => {
    if (!trailerFile) return;
    setUploading(true);
    setProgress(0);
    setStatus('Preparing upload...');
    const CHUNK_SIZE = 1 * 1024 * 1024;
    try {
      const fileSize = trailerFile.size;
      const totalChunks = Math.ceil(fileSize / CHUNK_SIZE);
      setStatus(`Preparing ${totalChunks} parts (${(fileSize / (1024 * 1024)).toFixed(1)}MB)...`);
      const initResponse = await axios.post(`${API}/upload/init`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          filename: trailerFile.name,
          file_size: fileSize,
          file_type: 'trailer',
          content_type: trailerFile.type || 'video/mp4',
          total_chunks: totalChunks,
          book_id: bookId,
          duration: trailerDuration,
        },
        timeout: 30000,
      });
      const uploadId = initResponse.data.upload_id;
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, fileSize);
        const chunk = trailerFile.slice(start, end);
        setProgress(Math.round((i / totalChunks) * 90));
        setStatus(`Uploading part ${i + 1} of ${totalChunks}... (${(chunk.size / 1024).toFixed(0)}KB)`);
        await uploadChunkWithRetry(uploadId, i, chunk, token, 3);
      }
      setProgress(92);
      setStatus('Starting video assembly...');
      try {
        await axios.post(`${API}/upload/complete/${uploadId}`, null, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000,
        });
      } catch (completeError) {
        if (completeError.code !== 'ECONNABORTED' && !completeError.message?.includes('timeout')) {
          throw completeError;
        }
      }
      setProgress(95);
      setStatus('Assembling video (this may take a few minutes for large files)...');
      let attempts = 0;
      const maxAttempts = 120;
      while (attempts < maxAttempts) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const statusResponse = await axios.get(`${API}/upload/status/${uploadId}`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 10000,
          });
          const s = statusResponse.data.status;
          if (s === 'completed') break;
          if (s === 'failed') throw new Error(statusResponse.data.error || 'Assembly failed');
          const elapsed = (attempts + 1) * 5;
          const minutes = Math.floor(elapsed / 60);
          const seconds = elapsed % 60;
          setStatus(`Assembling video... (${minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`})`);
        } catch {
          /* continue polling */
        }
        attempts++;
      }
      if (attempts >= maxAttempts) {
        throw new Error('Assembly timed out after 10 minutes. Please refresh the page.');
      }
      setProgress(100);
      setStatus('✓ Complete!');
      toast({ title: 'Trailer Uploaded!', description: `Successfully uploaded ${(fileSize / (1024 * 1024)).toFixed(1)}MB trailer.` });
      if (onUploaded) await onUploaded();
      setTimeout(() => {
        reset();
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Upload error:', error);
      setStatus('');
      setProgress(0);
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to upload trailer. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl p-6 w-full max-w-md border border-white/10">
        <h3 className="text-xl font-bold text-white mb-6 flex items-center">
          <Video className="h-6 w-6 text-purple-500 mr-2" />
          Upload Book Trailer
        </h3>
        <div className="space-y-4">
          <div>
            <Label className="text-white mb-2 block">Video File</Label>
            <p className="text-gray-500 text-sm mb-2">MP4 or WebM format. Max 2:30, 250MB.</p>
            <Input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/webm"
              onChange={handleSelect}
              className="bg-white/5 border-white/10 text-white file:bg-purple-600 file:text-white file:border-0 file:rounded-md file:mr-3"
              data-testid="trailer-file-input"
            />
          </div>
          {trailerError && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 flex items-start space-x-2">
              <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-300 text-sm">{trailerError}</p>
            </div>
          )}
          {trailerFile && !trailerError && (
            <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-3">
              <p className="text-green-300 text-sm">✓ {trailerFile.name} ({(trailerFile.size / 1024 / 1024).toFixed(2)} MB)</p>
              <p className="text-green-400 text-sm">
                Duration: {Math.floor(trailerDuration / 60)}:{String(Math.floor(trailerDuration % 60)).padStart(2, '0')}
              </p>
            </div>
          )}
        </div>
        <div className="flex space-x-3 mt-6">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="flex-1 border-white/20 text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!trailerFile || uploading || !!trailerError}
            className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
            data-testid="upload-trailer-btn"
          >
            {uploading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {progress > 0 ? `${progress}%` : 'Preparing...'}
              </span>
            ) : 'Upload Trailer'}
          </Button>
        </div>
        {uploading && (
          <div className="mt-4">
            <div className="w-full bg-white/10 rounded-full h-2">
              <div className="bg-purple-600 h-2 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
            <p className="text-gray-400 text-sm mt-2 text-center">{status}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrailerUploadModal;
